/** Pure display helpers for the wallet dashboard, extracted so they can be
 *  tested (the page component itself needs a DOM the `web-logic` vitest
 *  project deliberately does not have).
 *
 *  THIS FILE USED TO CARRY TWO MORE HELPERS, and why they went is worth
 *  keeping. `formatRateInfo` rendered the balance card's "estimated at $2.50/hr
 *  for spoken translation and $1.10/hr for subtitles", and `formatRemaining`
 *  rendered "about 4h 0m of spoken translation in total". The pricing card now
 *  answers both questions from a speaking-frequency slider across seven session
 *  shapes, which is a better answer than one hardcoded hourly figure.
 *
 *  Removing them also settled two problems those lines carried:
 *
 *  - They quoted the CONSERVATIVE ALLOWANCE rate while the pricing card quotes
 *    expected spend, so one page stated two figures about the same balance
 *    roughly three times apart.
 *  - The remaining-time line answered "how long will my balance last IN TOTAL"
 *    using the rate that gates a SINGLE session. A balance is debited by actual
 *    usage, not by the allowance, so that was the wrong rate for the question
 *    it was answering.
 *
 *  Do not reintroduce an hourly figure here without first deciding which of
 *  those two questions it answers — they have different right answers. */

/**
 * The `expiringSoon` field of /api/wallet/status, as the dashboard sees it.
 *
 * Named apart from the Worker's `ExpiringSoon` on purpose: that one is the
 * internal summary (`microUsd`, `expiresAt`), this one is the wire shape and
 * carries the pre-formatted `usd` as well. The two projects cannot import each
 * other, so one name for two shapes would be a trap rather than a link.
 */
export interface ExpiringSoonStatus {
  microUsd: number;
  usd: string;
  /** What expires on `expiresAt` itself; may be less than the horizon total. */
  microUsdAtEarliest: number;
  usdAtEarliest: string;
  expiresAt: number;
}

/**
 * The expiry line on the balance card, or null when there is nothing to say.
 *
 * MONEY AND A DATE, NEVER A DURATION. When this was written the card also
 * rendered "about {time} of spoken translation" from the conservative allowance
 * rate while the pricing card rendered an expected-spend figure from a
 * different model, and the two differed by roughly 2x; expressing the expiring
 * amount in hours would have made this a third voice in a disagreement it had
 * no stake in. The duration line has since been removed (see this file's header
 * for why), so the disagreement is gone — but the rule stands on its own. An
 * expiry is a deadline on an amount of money, and only the pricing card, which
 * knows what the user is actually doing, can turn money into hours honestly.
 *
 * A DATE WITHOUT A TIME, unlike `formatLedgerDate`. A ledger row is an event
 * that happened at a moment; an expiry is a deadline, and putting "14:32" on it
 * invites the reader to wonder whether the minutes matter. They do not — the
 * sweep runs daily, with a further day of grace.
 */
export function formatExpiryNotice(
  t: (key: string, fallback?: string) => string,
  expiringSoon: ExpiringSoonStatus | null | undefined,
  locale: string
): string | null {
  if (!expiringSoon) return null;
  const date = new Date(expiringSoon.expiresAt).toLocaleDateString(locale, {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  });

  // Two deadlines in the window means the total does NOT expire on this date,
  // and saying it does is a false claim about the reader's money. One date is
  // the ordinary case (a single top-up), and it keeps the plain sentence.
  if (expiringSoon.microUsdAtEarliest < expiringSoon.microUsd) {
    return t('dashboard.wallet.expiringSoonPartial', '${amount} expires on {date}, ${total} within a month')
      .replace('{amount}', expiringSoon.usdAtEarliest)
      .replace('{total}', expiringSoon.usd)
      .replace('{date}', date);
  }

  return t('dashboard.wallet.expiringSoon', '${amount} expires on {date}')
    .replace('{amount}', expiringSoon.usdAtEarliest)
    .replace('{date}', date);
}
